/**
 * JSON Folder-Based Template Renderer
 * Creates nested JSON objects based on folder hierarchy
 */

import { ExportTemplate, NamingConvention, TemplateField } from '../../../../types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { extractTextValue } from '../../core/formatting';
import { LookupContext } from '../../fieldMapping/references';
import { buildFolderBasedStructure, FolderNode, ItemFieldsNode } from '../../structure/folderBased';

/**
 * Format a field value for JSON output (native types)
 */
function formatJsonFieldValue(value: any, field: TemplateField): any {
  const extracted = extractTextValue(value);

  if (extracted === null || extracted === undefined || extracted === '') {
    if (field.mappedType.includes('number') || field.mappedType.includes('int')) return 0;
    if (field.mappedType.includes('boolean')) return false;
    if (field.mappedType.includes('array') || field.mappedType.includes('[]')) return [];
    return '';
  }

  if (Array.isArray(extracted)) {
    return extracted.map(v => {
      const ev = extractTextValue(v);
      if (typeof ev === 'number') return ev;
      if (typeof ev === 'boolean') return ev;
      return ev != null ? String(ev) : '';
    });
  }

  if (typeof extracted === 'number') return extracted;
  if (typeof extracted === 'boolean') return extracted;
  if (typeof extracted === 'string') return extracted;

  return String(extracted);
}

/**
 * Render folder-based JSON template
 * Creates nested JSON objects based on folder hierarchy
 *
 * @param items - Items to export
 * @param template - Export template configuration
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data for reference resolution
 * @param tags - Optional tags data for reference resolution
 * @param allItemsForLookup - Optional all items for glossaryItem lookups
 * @param namingConventionOverride - Optional override for all naming conventions
 * @param customClassName - Optional custom name for the root key (unused in JSON but kept for API compat)
 * @param dialogs - Optional dialogs array
 * @param documents - Optional documents array for document field resolution
 */
export function renderFolderBasedJsonTemplate(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customClassName?: string,
  dialogs?: any[],
  documents?: any[]
): string {
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items,
    documents: documents
  };

  const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;

  const structure = buildFolderBasedStructure(
    items, template, blueprints, folderPathResolver, lookupContext, namingConventionOverride
  );

  // Recursive function to convert the folder structure to a plain JSON-serializable object
  const convertNode = (node: FolderNode | ItemFieldsNode): any => {
    const result: Record<string, any> = {};

    for (const key of Object.keys(node)) {
      if (key.startsWith('_')) continue;

      const value = node[key];

      if (value && typeof value === 'object' && '_isItem' in value) {
        // Item node - output its fields as a plain object
        const itemNode = value as ItemFieldsNode;
        const itemObj: Record<string, any> = {};

        itemNode._fields.forEach(field => {
          let fieldName = applyNamingConvention(field.label, fieldConvention);

          if (!namingConventionOverride && template.fieldNameOverrides && itemNode._itemData.blueprint_id) {
            const blueprintOverrides = template.fieldNameOverrides[itemNode._itemData.blueprint_id];
            if (blueprintOverrides && blueprintOverrides[field.label]) {
              const override = blueprintOverrides[field.label];
              if (override.useExportName && override.exportName) {
                fieldName = override.exportName;
              }
            }
          }

          itemObj[fieldName] = formatJsonFieldValue(field.value, field);
        });

        result[key] = itemObj;
      } else if (value && typeof value === 'object') {
        // Folder node - recurse
        result[key] = convertNode(value as FolderNode);
      }
    }

    return result;
  };

  const jsonOutput = convertNode(structure);

  console.log('✅ Folder-based JSON template rendered successfully');

  return JSON.stringify(jsonOutput, null, 2);
}
